import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Sidebar } from "@/components/admin/Sidebar";
import { Dashboard } from "@/components/admin/Dashboard";
import { Leads } from "@/components/admin/Leads";
import { UnconfirmedLeadRegistry } from "@/components/admin/UnconfirmedLeadRegistry";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { Documents } from "@/components/admin/Documents";
import { Chancellery } from "@/components/admin/Chancellery";
import { Aml } from "@/components/admin/Aml";
import { Integrations } from "@/components/admin/Integrations";
import { Landings } from "@/components/admin/Landings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEAD_POLL_INTERVAL_MS = 15_000;
let leadAudioContext = null;

const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const urlBase64ToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const source = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(source, (character) => character.charCodeAt(0));
};

const prepareLeadAudio = async () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return false;
  try {
    if (!leadAudioContext || leadAudioContext.state === "closed") leadAudioContext = new AudioContextClass();
    if (leadAudioContext.state === "suspended") await leadAudioContext.resume();
    return leadAudioContext.state === "running";
  } catch {
    return false;
  }
};

const playLeadTone = () => {
  const context = leadAudioContext;
  if (!context || context.state !== "running") return false;
  try {
    const gain = context.createGain();
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.setValueAtTime(1175, context.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.32);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.34);
    return true;
  } catch {
    return false;
  }
};

const notifyBrowser = (title, body, onOpen) => {
  if (!("Notification" in window) || Notification.permission !== "granted") return false;
  try {
    const notification = new Notification(title, { body, icon: "/favicon.ico", tag: "melnik-new-lead", renotify: true });
    notification.onclick = () => {
      window.focus();
      onOpen?.();
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
};

export default function Admin() {
  const storedAccessCode = sessionStorage.getItem("desk_access_code") || "";
  const [accessCode, setAccessCode] = useState(storedAccessCode);
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(Boolean(storedAccessCode));
  const [error, setError] = useState("");
  const [tab, setTab] = useState(() => sessionStorage.getItem("desk_active_tab") || "dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [leadSummary, setLeadSummary] = useState(null);
  const [leadSoundEnabled, setLeadSoundEnabled] = useState(() => localStorage.getItem("desk_lead_notification_sound") !== "off");
  const [notificationPermission, setNotificationPermission] = useState(() => ("Notification" in window ? Notification.permission : "unsupported"));
  const knownLeadIds = useRef(null);
  const leadSoundEnabledRef = useRef(leadSoundEnabled);

  const api = useMemo(
    () => axios.create({ baseURL: API, headers: { "x-admin-code": accessCode } }),
    [accessCode]
  );

  useEffect(() => {
    leadSoundEnabledRef.current = leadSoundEnabled;
    localStorage.setItem("desk_lead_notification_sound", leadSoundEnabled ? "on" : "off");
  }, [leadSoundEnabled]);

  const armLeadAlerts = useCallback(async () => {
    await prepareLeadAudio();
    if (!("Notification" in window)) {
      setNotificationPermission("unsupported");
      return "unsupported";
    }
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    }
    setNotificationPermission(Notification.permission);
    return Notification.permission;
  }, []);

  const toggleLeadSound = useCallback(async () => {
    const enabled = !leadSoundEnabled;
    setLeadSoundEnabled(enabled);
    if (enabled) await armLeadAlerts();
  }, [armLeadAlerts, leadSoundEnabled]);

  const testLeadAlerts = useCallback(async () => {
    if (!leadSoundEnabled) setLeadSoundEnabled(true);
    const permission = await armLeadAlerts();
    playLeadTone();
    let pushSent = false;
    let pushError = "";
    if (permission === "granted" && pushSupported()) {
      try {
        const registration = await navigator.serviceWorker.register("/push-sw.js");
        await navigator.serviceWorker.ready;
        const keyResponse = await api.get("/admin/push/public-key");
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(keyResponse.data.public_key) });
        }
        await api.post("/admin/push/subscriptions", subscription.toJSON());
        const response = await api.post("/admin/push/test", { endpoint: subscription.endpoint });
        pushSent = Boolean(response.data.delivered);
        if (!pushSent) pushError = "Сервер не подтвердил доставку Web Push.";
      } catch (error) {
        pushError = error?.response?.data?.detail || "Браузер не смог создать подписку Web Push.";
      }
    }
    if (permission === "granted") notifyBrowser("MELNIK — проверка уведомлений", "Уведомления о новых заявках активны.", () => setTab("leads"));
    if (pushSent) {
      toast.success("Проверка уведомлений", { description: "Звук и Web Push отправлены." });
    } else if (permission !== "granted") {
      toast.message("Звук активирован", { description: "Разрешите browser-уведомления в системном окне, чтобы получать их вне вкладки." });
    } else if (!pushSupported()) {
      toast.message("Звук активирован", { description: "Этот браузер не поддерживает Web Push. Browser-уведомления работают, пока панель открыта." });
    } else {
      toast.error("Web Push не подключён", { description: pushError || "Повторите проверку после разрешения уведомлений браузера." });
    }
  }, [api, armLeadAlerts, leadSoundEnabled]);

  const refreshLeadSummary = useCallback(async () => {
    const response = await api.get("/leads/notification-summary");
    const summary = response.data;
    const latest = Array.isArray(summary.latest) ? summary.latest : [];
    const currentIds = new Set(latest.map((lead) => lead.id).filter(Boolean));

    if (knownLeadIds.current === null) {
      knownLeadIds.current = currentIds;
      setLeadSummary(summary);
      return summary;
    }

    const newlyArrived = latest.filter((lead) => lead.id && !knownLeadIds.current.has(lead.id));
    knownLeadIds.current = currentIds;
    setLeadSummary(summary);

    if (newlyArrived.length) {
      if (leadSoundEnabledRef.current) playLeadTone();
      notifyBrowser(
        newlyArrived.length === 1 ? "MELNIK — новая заявка" : `MELNIK — новых заявок: ${newlyArrived.length}`,
        "Откройте панель для обработки.",
        () => setTab("leads"),
      );
      toast.success(newlyArrived.length === 1 ? "Новая заявка" : `Новых заявок: ${newlyArrived.length}`, {
        description: "Откройте раздел «Заявки» для обработки.",
        duration: 9_000,
        action: { label: "Открыть", onClick: () => setTab("leads") },
      });
    }
    return summary;
  }, [api]);

  const login = async (code) => {
    if (leadSoundEnabled) void prepareLeadAudio();
    setChecking(true);
    setError("");
    try {
      await axios.get(`${API}/admin/auth-check`, { headers: { "x-admin-code": code } });
      sessionStorage.removeItem("desk_username");
      sessionStorage.removeItem("desk_password");
      sessionStorage.setItem("desk_access_code", code);
      setAccessCode(code);
      setAuthed(true);
    } catch {
      setError("Авторизация временно недоступна. Обратитесь к администратору.");
      sessionStorage.removeItem("desk_access_code");
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    sessionStorage.removeItem("desk_username");
    sessionStorage.removeItem("desk_password");
    const storedCode = sessionStorage.getItem("desk_access_code") || "";
    if (storedCode) login(storedCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { sessionStorage.setItem("desk_active_tab", tab); }, [tab]);

  useEffect(() => {
    if (!authed) return undefined;
    const unlock = () => { void prepareLeadAudio(); };
    window.addEventListener("pointerdown", unlock, { once: true, capture: true });
    window.addEventListener("keydown", unlock, { once: true, capture: true });
    return () => {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    };
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    const newCount = leadSummary?.counts?.new || 0;
    document.title = newCount ? `(${newCount}) Новые заявки — MELNIK` : "MELNIK Commercial Brokers";
  }, [authed, leadSummary]);

  useEffect(() => {
    if (!authed) {
      knownLeadIds.current = null;
      setLeadSummary(null);
      return undefined;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        if (!cancelled) await refreshLeadSummary();
      } catch {
        // Polling retries quietly; manual work in the panel is unaffected.
      }
    };
    refresh();
    const intervalId = window.setInterval(refresh, LEAD_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authed, refreshLeadSummary]);

  const logout = () => {
    sessionStorage.removeItem("desk_access_code");
    knownLeadIds.current = null;
    setLeadSummary(null);
    setAccessCode("");
    setAuthed(false);
  };

  const applyLeadCounts = useCallback((counts) => {
    if (!counts) return;
    setLeadSummary((current) => ({ ...(current || {}), counts, new_count: counts.new || 0 }));
  }, []);

  if (checking && !authed) {
    return <div data-testid="admin-auth-checking" className="flex min-h-screen items-center justify-center bg-[#0A0B0E]"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>;
  }

  if (!authed) {
    return (
      <div data-testid="admin-page" className="flex min-h-screen items-center justify-center bg-[#0A0B0E] px-4 text-slate-100">
        <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-[#12141C] p-10">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
            <h1 className="font-serif text-2xl text-slate-50">Вход для команды</h1>
          </div>
          <p className="mt-3 text-sm text-slate-500">Панель управления MELNIK COMMERCIAL BROKERS. Введите код доступа.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              login(accessCode);
            }}
            className="mt-8 space-y-4"
          >
            <input
              type="password"
              inputMode="numeric"
              autoComplete="current-password"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Код доступа"
              data-testid="admin-code-input"
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-[#D4AF37]/60"
            />
            {error && (
              <p data-testid="admin-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={checking}
              data-testid="admin-login-button"
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#D4AF37] px-8 py-4 text-sm font-semibold text-[#0A0B0E] transition-all hover:shadow-[0_0_40px_rgba(212,175,55,0.3)] disabled:opacity-60"
            >
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Войти"}
            </button>
          </form>
          <p className="mt-4 text-center text-xs text-slate-600">Введите код доступа для продолжения.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#0A0B0E] text-slate-100 md:flex">
      <Sidebar
        tab={tab}
        setTab={setTab}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        onLogout={logout}
        leadSummary={leadSummary}
        leadSoundEnabled={leadSoundEnabled}
        setLeadSoundEnabled={toggleLeadSound}
        onLeadAlertsTest={testLeadAlerts}
        notificationPermission={notificationPermission}
      />
      <main className="min-w-0 w-full flex-1 px-4 py-5 sm:px-6 sm:py-8 md:px-8 lg:px-12">
        {tab === "dashboard" && <Dashboard api={api} />}
        {tab === "leads" && <Leads api={api} summary={leadSummary} onSummaryRefresh={refreshLeadSummary} onLeadCountsChange={applyLeadCounts} />}
        {tab === "registry" && <UnconfirmedLeadRegistry api={api} />}
        {tab === "landings" && <Landings api={api} />}
        {tab === "settings" && <SettingsTab api={api} />}
        {tab === "documents" && <Documents api={api} />}
        {tab === "chancellery" && <Chancellery api={api} />}
        {tab === "aml" && <Aml api={api} setTab={setTab} />}
        {tab === "integrations" && <Integrations setTab={setTab} api={api} />}
      </main>
    </div>
  );
}
